import React, {ReactElement} from "react"
import {ManagedSolutionsAccount} from "../../resources/managedSolutions"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import classNames from "classnames"
import {startCase} from "lodash"
import moment from "moment"
import {Meta} from "../../resources/common"
import numeral from "numeral"
import {ManagedSolutionsAccountsColumns, AllowedManagedSolutionsAccountsColumns} from "./ManagedSolutionsAccounts.types"
import {accountNumberPreview, InformationHiding} from "../InformationHiding/InformationHiding"
import {useIsBankUser, useIsOrgManagedSolutionsOperationalAdminOnly} from "../../services/auth"
import {useNavigate} from "react-router-dom"

interface ManagedSolutionsAccountsTableProps {
	accounts: Array<ManagedSolutionsAccount>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	fullHeight?: boolean
	meta?: Meta
	includedColumns: Array<AllowedManagedSolutionsAccountsColumns>
	sortBy?: string
	sortFunction?: () => void
}

function getDisplayType(type: string | undefined): string {
	if (!type) return "-"
	return type == "fboAccount" ? "Org FBO Account" : startCase(type)
}

export default function ManagedSolutionsAccountsTable({
	accounts,
	hasPrev,
	hasNext,
	prev,
	next,
	hasResults,
	isUsingPaging,
	fullHeight,
	meta,
	includedColumns,
	sortBy,
	sortFunction,
}: ManagedSolutionsAccountsTableProps) {
	const noContent = accounts.length === 0
	const isBankUser = useIsBankUser()
	const isOperationalAdminOnly = useIsOrgManagedSolutionsOperationalAdminOnly()
	const navigate = useNavigate()

	return (
		<div className={"managed-solutions-accounts-table"}>
			<DataTable isEmpty={noContent} fullHeight={fullHeight} noContentText="No accounts yet">
				<DataTableHead className={classNames(!fullHeight && "no-sticky")}>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === ManagedSolutionsAccountsColumns.createdAt) {
								return (
									<DataTableCell
										key={column[0]}
										clickable
										onClick={() => sortFunction?.()}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{accounts.map((account) => {
						const accountType = account.attributes.accountType
						const status = account.attributes.status
						const createdAt = account.attributes.createdAt
						const balance = account.attributes.balance
						const isOperationalAccount = account.attributes.tags?.purpose === "operational_account"
						const mayShowBalance = !isOperationalAdminOnly || isOperationalAccount
						const routingNumber = account.attributes.routingNumber
						const accountNumber = account.attributes.accountNumber

						const contentColumns: Partial<Record<AllowedManagedSolutionsAccountsColumns, ReactElement>> = {}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.id)) {
							contentColumns["Id"] = <DataTableCell className="data-table-id-cell">{account.id}</DataTableCell>
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.userIds)) {
							contentColumns["User IDs"] = (
								<DataTableCell>
									{account.attributes.userIds.map((userId, index) => (
										<div key={index}>{userId}</div>
									))}
								</DataTableCell>
							)
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.type)) {
							contentColumns["Type"] = <DataTableCell>{accountType ? getDisplayType(accountType) : "-"}</DataTableCell>
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.createdAt)) {
							contentColumns["Created At"] = (
								<DataTableCell>{createdAt ? moment(createdAt).format("L LT") : "-"}</DataTableCell>
							)
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.status)) {
							contentColumns["Status"] = (
								<DataTableCell>
									<div className={classNames("accounts-status", classNames(status.toLowerCase().replace(/\s+/g, "-")))}>
										{startCase(status)}
									</div>
								</DataTableCell>
							)
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.balance)) {
							contentColumns["Balance"] = (
								<DataTableCell
									className={classNames(mayShowBalance && balance !== undefined && balance < 0 && "has-text-danger")}
								>
									{mayShowBalance && balance !== undefined ? numeral(balance / 100).format("$0,0.00") : "—"}
								</DataTableCell>
							)
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.routingNumber)) {
							contentColumns["Routing Number"] = <DataTableCell>{routingNumber}</DataTableCell>
						}

						if (includedColumns.includes(ManagedSolutionsAccountsColumns.accountNumber)) {
							if (accountNumber) {
								contentColumns["Account Number"] = (
									<DataTableCell>
										<InformationHiding
											element={(accountNumber) => <>{accountNumber}</>}
											getValue={() => accountNumber ?? ""}
											placeholder={accountNumberPreview(accountNumber)}
											defaultIsHidden={!isBankUser}
										/>
									</DataTableCell>
								)
							} else {
								contentColumns["Account Number"] = <DataTableCell>{"-"}</DataTableCell>
							}
						}

						const content: Array<ReactElement | null> = []

						includedColumns.forEach((col, i) => {
							if (col in contentColumns && contentColumns[col]) {
								const column = {...contentColumns[col]} as ReactElement
								column.key = i.toString()
								content.push(column)
							}
						})

						return (
							<DataTableRow
								key={account.id}
								onClick={(e) => {
									e.preventDefault()
									navigate(`/managed-solutions/accounts/${account.id}`)
								}}
							>
								{content}
							</DataTableRow>
						)
					})}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>
		</div>
	)
}
